import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const GOLD = colors.primary.main;
const MOBILE_NAV_MQ = `@media ${MOBILE_NAV_MEDIA}`;

export default function MapThumbCard({
    imageUrl,
    label,
    checked = false,
    compact = false,
    disabled = false,
    onClick,
}) {
    useEffect(() => {
        if (!imageUrl) {
            return undefined;
        }
        const img = new Image();
        img.src = imageUrl;
        return undefined;
    }, [imageUrl]);

    const interactive = typeof onClick === "function" && !disabled;

    return (
        <Box
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={interactive ? checked : undefined}
            aria-label={label}
            onClick={
                interactive
                    ? () => {
                          onClick();
                      }
                    : undefined
            }
            onKeyDown={
                interactive
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            sx={{
                position: "relative",
                width: compact ? "100px" : "100%",
                height: compact ? "3.75rem" : "7.5rem",
                cursor: disabled ? "default" : interactive ? "pointer" : "default",
                overflow: "hidden",
                clipPath: compact
                    ? "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)"
                    : "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)",
                border: "none",
                backgroundColor: colors.background.dark,
                boxShadow: checked
                    ? `inset 0 1px 0 ${alpha(GOLD, 0.18)}`
                    : `
                        inset 0 1px 0 rgba(255,255,255,0.045),
                        inset 0 -1px 0 rgba(0,0,0,0.55)
                    `,
                opacity: disabled ? 0.55 : 1,
                pointerEvents: disabled ? "none" : "auto",
                transition: "box-shadow 0.15s ease-in-out",
                flexShrink: 0,
                [MOBILE_NAV_MQ]: {
                    width: compact ? "84px" : "100%",
                    height: compact ? "2.75rem" : "6.25rem",
                },
                "&:hover":
                    interactive && !disabled
                        ? {
                              boxShadow: checked
                                  ? `inset 0 1px 0 ${alpha(GOLD, 0.28)}`
                                  : `
                                      inset 0 1px 0 ${alpha(GOLD, 0.16)},
                                      inset 0 0 0 1px ${alpha(GOLD, 0.12)}
                                  `,
                          }
                        : undefined,
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
                    backgroundColor: imageUrl
                        ? undefined
                        : "rgba(255, 255, 255, 0.06)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    filter: checked
                        ? "saturate(1) brightness(0.88)"
                        : "saturate(0.55) brightness(0.55)",
                    transition: "filter 0.15s ease-in-out",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        linear-gradient(
                            0deg,
                            rgba(10, 10, 15, 0.82) 0%,
                            rgba(10, 10, 15, 0.18) 48%,
                            transparent 70%
                        )
                    `,
                    pointerEvents: "none",
                }}
            />

            <Typography
                sx={{
                    position: "absolute",
                    left: compact ? "0.4rem" : "0.75rem",
                    bottom: compact ? "0.28rem" : "0.5rem",
                    right: compact ? "0.35rem" : "auto",
                    zIndex: 1,
                    color: colors.text.primary,
                    fontSize: compact ? "0.68rem" : "0.95rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    lineHeight: 1.15,
                    textShadow: "0 2px 6px rgba(0, 0, 0, 0.75)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    [MOBILE_NAV_MQ]: {
                        left: compact ? "0.32rem" : "0.6rem",
                        bottom: compact ? "0.22rem" : "0.4rem",
                        fontSize: compact ? "0.6rem" : "0.85rem",
                    },
                }}
            >
                {label}
            </Typography>

            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: compact ? "0.22rem" : "0.45rem",
                    right: compact ? "0.22rem" : "0.45rem",
                    zIndex: 2,
                    width: compact ? "0.85rem" : "1.25rem",
                    height: compact ? "0.85rem" : "1.25rem",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: checked ? GOLD : "rgba(8, 8, 10, 0.55)",
                    border: checked
                        ? `1.5px solid ${GOLD}`
                        : `1px solid ${alpha("#fff", 0.12)}`,
                    boxShadow: checked
                        ? `
                            0 1px 2px rgba(0, 0, 0, 0.4),
                            0 0 8px ${alpha(GOLD, 0.35)}
                        `
                        : "none",
                    [MOBILE_NAV_MQ]: {
                        top: compact ? "0.18rem" : "0.38rem",
                        right: compact ? "0.18rem" : "0.38rem",
                        width: compact ? "0.7rem" : "1.1rem",
                        height: compact ? "0.7rem" : "1.1rem",
                    },
                }}
            >
                {checked ? (
                    <CheckIcon
                        sx={{
                            fontSize: compact ? "0.52rem" : "0.78rem",
                            color: colors.text.contrast,
                            [MOBILE_NAV_MQ]: {
                                fontSize: compact ? "0.44rem" : "0.7rem",
                            },
                        }}
                    />
                ) : null}
            </Box>

            {checked ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: compact ? "2px" : "2px",
                        zIndex: 2,
                        background: GOLD,
                        boxShadow: `
                            0 0 8px ${alpha(GOLD, 0.55)},
                            0 3px 10px ${alpha(GOLD, 0.28)}
                        `,
                    }}
                />
            ) : null}
        </Box>
    );
}

MapThumbCard.propTypes = {
    imageUrl: PropTypes.string,
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool,
    compact: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
};
